<template>
  <PublicLayout
    variant="no-bg"
    :breadcrumbs="breadcrumbs"
    :hero="false"
    breadcrumbs-in-content
    :ui="{
      main: 'pt-0!',
      contentBreadcrumbs: 'mb-3!',
    }"
  >
    <UContainer class="page-container">
      <PageContentSlider
        class="mb-10"
        title="Подобрать тренера"
        description="Выберите интересующие вас параметры, чтобы найти подходящую школу"
        :counter="{
          amount: '500 тыс',
          text: 'Школ на портале',
        }"
        to-prefix="/trainers"
        image="/images/schools/cup.webp"
        :slides="trainersWithLinks"
        location-badge
        :loading="pending"
      >
        <template #slide-badges="{ slide }">
          <UBadge
            v-for="(badge, index) in slide?.lesson_types"
            :key="index"
            class="rounded-[250px] text-white! ring-white bg-transparent"
            variant="subtle"
          >
            {{ badge.name }}
          </UBadge>
        </template>

        <template #slide-button="{ slide }">
          <UButton
            class="mb-10.5 max-lg:w-max text-button-secondary-foreground max-sm:hidden"
            trailing-icon="i-local-move-up-right"
            color="white"
            :to="'/trainers/' + slide.id"
            :ui="{
              trailingIcon: 'text-button-secondary-foreground',
            }"
          >
            Подробнее
          </UButton>
        </template>
      </PageContentSlider>

      <PageSearchFilter
        v-model:directions="selectedDirections"
        v-model:lesson-types="selectedLessonTypes"
        v-model:query="query"
        class="mb-10"
        placeholder="Найти тренера"
        @search="search"
      />

      <ExpandableList v-slot="{ displayItems }" :items="trainers" :limit="9">
        <CommonGridCards :items="displayItems" :pending="pending">
          <template #card="{ item }">
            <CommonCard
              :item="item"
              :to="`/trainers/${item.id}`"
              :description-under-left="descriptionUnder"
              image-top-left="directions"
              title-above-left="school"
              title-right="directions"
              :ui="{
                title: 'max-sm:line-clamp-1',
              }"
            >
            </CommonCard>
          </template>
        </CommonGridCards>
      </ExpandableList>
    </UContainer>

    <JoinUsSection />
  </PublicLayout>
</template>

<script setup lang="ts">
import type { LessonTypeRef } from '~/api'
import JoinUsSection from '~/components/main-page/JoinUsSection.vue'
import PublicLayout from '~/layouts/PublicLayout.vue'
import ExpandableList from '~/components/ExpandableList.vue'
import type {
  BreadcrumbItem,
  CardLink,
  DirectionMenuItem,
  IconWithLabel,
  SearchParams,
} from '~/types'

defineOptions({
  name: 'TrainersPage',
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    name: 'Главная',
    to: '/',
  },
  {
    name: 'Тренеры',
  },
]

definePageMeta({
  breadcrumbs,
})

const route = useRoute()
const directoryStore = useDirectoryStore()
const { trainers } = storeToRefs(directoryStore)

const { untilReady } = useRegionReady()

const { pending } = await useAppAsyncData(
  `trainers-${route.path}`,
  async () => {
    await untilReady()
    await Promise.all([
      directoryStore.fetchDirections(),
      directoryStore.fetchLessonTypes(),
      directoryStore.fetchTrainers(),
    ])

    return true
  },
)

const descriptionUnder: IconWithLabel[] = [
  {
    icon: 'i-local-graduation-cap',
    label: 'Опыт: ',
    value: '24 мока',
    iconClass: '',
  },
  {
    icon: 'i-local-award',
    label: 'Награды: ',
    value: '104',
    iconClass: 'bg-warning-light',
  },
]

const selectedDirections = ref<DirectionMenuItem[]>([])
const selectedLessonTypes = ref<LessonTypeRef[]>([])
const query = ref('')
const trainersWithLinks = computed(() =>
  trainers.value.map((trainer) => ({
    ...trainer,
    links: [
      {
        icon: 'i-local-graduation-cap',
        text: 'Моковое название школы',
        color: 'bg-accent-default',
      },
      {
        icon: 'i-local-location',
        text: trainer.region?.name ?? '',
        color: 'bg-warning-light',
      },
    ] as CardLink[],
  })),
)

const search = async (query?: SearchParams) => {
  await directoryStore.fetchTrainers(query)
}
</script>

<style lang="scss" scoped>
.page-container {
  margin-bottom: var(--page-section-padding);
}

:deep(.common-card) {
  --card-height: 456px;
  --card-image-height: 240px;

  @include max-xl {
    --card-height: 380px;
    --card-image-height: 200px;
  }

  @include max-md {
    --card-height: 330px;
    --card-image-height: 160px;

    :deep(.body-header) {
      display: none;
    }
  }
}

:deep(.link-badge) {
  max-width: 50%;
  min-width: unset;

  .link-text {
    font-size: 12px !important;
  }
}

:deep(.badge-icon) {
  width: 24px;
  height: 24px;
}

:deep(.swiper-slide) {
  background-position: right top;
}

:deep(.featured-card) {
  @include max-md {
    max-height: 195px;
  }
}
</style>
